<template>
  <EntitySingle
    :config="config"
    :entity.sync="entity"
    :loaded.sync="entityLoaded"
    :extra-validation="extraValidation"
    :extra-actions="extraActions"
    @entityFetched="updateEntity"
  >
    <b-card v-if="entityLoaded">
      <b-tabs pills>
        <!-- Tab: Account -->
        <b-tab active>
          <template #title>
            <feather-icon
              icon="UserIcon"
              size="16"
              class="mr-0 mr-sm-50"
            />
            <span class="d-none d-sm-inline">{{ $t('Account') }}</span>
          </template>
          <EntityEditSection
            :module="config.module"
            :entity.sync="entity"
            :form-fields="accountFormFields"
            allow-autofocus
          />
        </b-tab>

        <!-- Tab: Information -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="InfoIcon"
              size="16"
              class="mr-0 mr-sm-50"
            />
            <span class="d-none d-sm-inline">{{
              $t('Contact Information')
            }}</span>
          </template>
          <EntityEditSection
            :module="config.module"
            :entity.sync="entity"
            :form-fields="contactInformationFormFields"
          />
        </b-tab>

        <!-- Tab: Job Details -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="InfoIcon"
              size="16"
              class="mr-0 mr-sm-50"
            />
            <span class="d-none d-sm-inline">{{ $t('Job Details') }}</span>
          </template>
          <EntityEditSection
            :module="config.module"
            :entity.sync="entity"
            :form-fields="jobDetailsFormFields"
          />
        </b-tab>

        <!-- Tab: Social Links -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="LinkIcon"
              size="16"
              class="mr-0 mr-sm-50"
            />
            <span class="d-none d-sm-inline">{{ $t('Links') }}</span>
          </template>
          <EntityEditSection
            :module="config.module"
            :entity.sync="entity"
            :form-fields="socialFormFields"
          />
        </b-tab>

        <!-- Tab: Subscription -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="SlidersIcon"
              size="16"
              class="mr-0 mr-sm-50"
            />
            <span class="d-none d-sm-inline">{{ $t('Subscription') }}</span>
          </template>
          <EntityEditSection
            :module="config.module"
            :entity.sync="entity"
            :form-fields="subscriptionFields"
          />
        </b-tab>

        <!-- Tab: Payments -->
        <b-tab
          v-if="entity.id > 0"
          lazy
        >
          <template #title>
            <feather-icon
              icon="CreditCardIcon"
              size="16"
              class="mr-0 mr-sm-50"
            />
            <span class="d-none d-sm-inline">{{ $t('Payments') }}</span>
          </template>
          <UserPaymentsList
            :module="config.module"
            :parent-entity.sync="entity"
          />
        </b-tab>
      </b-tabs>
    </b-card>
  </EntitySingle>
</template>

<script>
import { translationUtils } from '@core/mixins/ui/translations'
import EntityEditSection from '@/layouts/entity/EntityEditSection.vue'
import EntitySingle from '@/layouts/entity/EntitySingle.vue'
import storeModule from '../userStoreModule'
import UserPaymentsList from './UserPaymentsList.vue'

export default {
  components: {
    EntityEditSection,
    EntitySingle,
    UserPaymentsList,
  },
  mixins: [translationUtils],
  data() {
    return {
      entity: {
        participant: {
          country: { id: 263, title: 'Greece' },
        },
        enabled: true,
        memberType: { id: 1, title: 'Τακτικό' },
      },
      entityLoaded: false,
      countryOptions: [],
      companyOptions: [],
      userGroupOptions: [],
      secondaryGroupOptions: [],
      workingGroupOptions: [],
      specialtiesOptions: [],
      prefectureOptions: [],
      professionOptions: [],
      memberTypeOptions: [],
      hospitalTypeOptions: [],
      hospitalOptions: [],
      clinicOptions: [],
      positionOptions: [],
      hasErpId: true,
      genderOptions: [
        { id: 1, title: 'Άνδρας' },
        { id: 2, title: 'Γυναίκα' },
      ],
    }
  },
  computed: {
    config() {
      return {
        store: storeModule,
        module: 'users',
        endpoint: 'users',
        route: 'users',
        title: {
          single: this.$t('User'),
          plural: this.$t('Users'),
        },
      }
    },

    accountFormFields() {
      return [
        {
          id: 'profilePhoto',
          parent: 'participant',
          type: 'image',
          label: `${this.$t('Image')} (512x512)`,
          colSize: 3,
        },
        {
          id: 'userGroup',
          object: true,
          type: 'select',
          label: this.$t('Member-Type'),
          options: this.userGroupOptions,
          required: true,
          colSize: 3,
        },
        {
          id: 'secondaryGroups',
          type: 'selectmultiple',
          label: this.$t('secondaryGroups'),
          options: this.secondaryGroupOptions,
          colSize: 3,
        },
        {
          id: 'memberCode',
          type: 'text',
          label: this.$t('Member Code'),
          colSize: 3,
        },
        {
          id: 'firstName',
          parent: 'participant',
          type: 'text',
          label: this.$t('First Name'),
          required: true,
          rules: 'min:3',
          colSize: 3,
        },
        {
          id: 'lastName',
          parent: 'participant',
          type: 'text',
          label: this.$t('Last Name'),
          required: true,
          rules: 'min:3',
          colSize: 3,
        },
        {
          id: 'email',
          parent: 'participant',
          type: 'email',
          label: this.$t('email'),
          required: true,
          rules: 'email',
          colSize: 3,
        },
        {
          id: 'erpId',
          type: 'text',
          label: this.$t('memberNr'),
          readonly: this.hasErpId,
          colSize: 3,
        },
        {
          id: 'profession',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('Profession'),
          options: this.professionOptions,
          colSize: 3,
        },
        {
          id: 'specialty',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('Specialty'),
          options: this.specialtiesOptions,
          colSize: 3,
        },
        {
          id: 'specialtyYear',
          parent: 'participant',
          type: 'number',
          label: this.$t('specialtyYear'),
          rules: 'min:4|max:4|positive',
          colSize: 2,
        },
        {
          id: 'birthYear',
          parent: 'participant',
          type: 'number',
          label: this.$t('Birth Year'),
          colSize: 2,
          rules: 'min:4|max:4|positive',
        },
        {
          id: 'email2',
          parent: 'participant',
          type: 'email',
          // eslint-disable-next-line prefer-template
          label: this.$t('email') + ' 2',
          rules: 'email',
          colSize: 2,
        },
        {
          id: 'gender',
          parent: 'participant',
          type: 'select',
          label: this.$t('gender'),
          options: this.genderOptions,
          colSize: 3,
        },
        {
          id: 'registrationDate',
          type: 'date',
          label: this.$t('Registration Date'),
          colSize: 3,
          maxDate: new Date(),
        },
        {
          id: 'workingGroups',
          object: true,
          type: 'selectmultiple',
          label: this.$t('Working Groups'),
          options: this.workingGroupOptions,
          colSize: 6,
        },
        {
          id: 'comments',
          parent: 'participant',
          type: 'quill',
          label: this.$t('comments'),
          colSize: 6,
        },
        {
          id: 'enabled',
          type: 'checkbox',
          label: this.$t('Enabled'),
          colSize: 1,
        },
        {
          id: 'changePassword',
          type: 'checkbox',
          label: this.$t('change password'),
          hidden: !this.entity.id,
          colSize: 2,
        },
        {
          id: 'password',
          type: 'password',
          label: this.$t('password'),
          required: !this.entity.id || this.entity.changePassword,
          rules: 'password',
          colSize: 3,
          dependency: this.entity.id ? 'changePassword' : null,
        },
      ]
    },
    contactInformationFormFields() {
      return [
        {
          id: 'country',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('country'),
          options: this.countryOptions,
          required: true,
          colSize: 3,
        },
        {
          id: 'prefecture',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('prefecture'),
          options: this.prefectureOptions,
          colSize: 3,
        },
        {
          id: 'city',
          parent: 'participant',
          type: 'text',
          label: this.$t('city'),
          colSize: 3,
        },
        {
          id: 'region',
          parent: 'participant',
          type: 'text',
          label: this.$t('region'),
          colSize: 3,
        },
        {
          id: 'address',
          parent: 'participant',
          type: 'text',
          label: this.$t('address'),
          required: false,
          colSize: 3,
        },
        {
          id: 'postalCode',
          parent: 'participant',
          type: 'text',
          label: this.$t('postal code'),
          required: false,
          colSize: 3,
        },
        {
          id: 'mobile',
          parent: 'participant',
          type: 'text',
          label: this.$t('mobile'),
          rules: 'phone',
          colSize: 3,
        },
        {
          id: 'telephone',
          parent: 'participant',
          type: 'text',
          label: this.$t('telephone'),
          rules: 'phone',
          colSize: 3,
        },
        {
          id: 'bio',
          parent: 'participant',
          type: 'quill',
          label: this.$t('bio'),
          required: false,
          colSize: 12,
        },
        {
          id: 'map',
          type: 'map',
          colSize: 12,
        },
      ]
    },
    jobDetailsFormFields() {
      return [
        {
          id: 'jobTelephone',
          parent: 'participant',
          type: 'text',
          label: this.$t('telephonebusiness'),
          rules: 'phone',
          colSize: 3,
        },
        {
          id: 'companyName',
          parent: 'participant',
          type: 'text',
          label: this.$t('company name'),
          colSize: 3,
        },
        {
          id: 'vatId',
          parent: 'participant',
          type: 'text',
          label: this.$t('vatId'),
          required: false,
          colSize: 3,
        },
        {
          id: 'vatOffice',
          parent: 'participant',
          type: 'text',
          label: this.$t('vat office'),
          required: false,
          colSize: 3,
        },
        {
          id: 'hospital',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('hospital'),
          options: this.hospitalOptions,
          colSize: 3,
        },
        {
          id: 'clinic',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('clinic'),
          options: this.clinicOptions,
          colSize: 3,
        },
        {
          id: 'jobPosition',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('JobPosition'),
          options: this.positionOptions,
          colSize: 3,
        },
        {
          id: 'jobPrefecture',
          object: true,
          parent: 'participant',
          type: 'select',
          label: this.$t('JobPrefecture'),
          options: this.prefectureOptions,
          colSize: 3,
        },
        {
          id: 'privateOffice',
          parent: 'participant',
          type: 'checkbox',
          label: this.$t('PrivateOffice'),
          colSize: 1,
        },
        {
          id: 'privateClinic',
          parent: 'participant',
          type: 'checkbox',
          label: this.$t('PrivateClinic'),
          colSize: 1,
        },
        {
          id: 'professionalTitle',
          parent: 'participant',
          type: 'quill',
          label: this.$t('professional title'),
          required: false,
          colSize: 7,
        },
      ]
    },
    socialFormFields() {
      return [
        {
          id: 'orcid',
          noParent: true,
          noUrl: true,
          label: this.$t('orcid'),
        },
        {
          id: 'escId',
          noParent: true,
          noUrl: true,
          label: this.$t('escId'),
        },
        {
          id: 'google',
          label: 'Google',
        },
        {
          id: 'facebook',
          label: 'Facebook',
        },
        {
          id: 'instagram',
          label: 'Instagram',
        },
        {
          id: 'linkedIn',
          label: 'LinkedIn',
        },
        {
          id: 'twitter',
          label: 'Twitter',
        },
        {
          id: 'pinterest',
          label: 'Pinterest',
        },
        {
          id: 'youtube',
          label: 'Youtube',
        },
      ].map(field => ({
        ...field,
        type: field.type || 'text',
        parent: field.noParent ? null : 'participant',
        required: false,
        rules: field.noUrl ? null : 'url',
        colSize: 4,
      }))
    },
    subscriptionFields() {
      return [
        {
          id: 'subscriptionStartDate',
          type: 'date',
          label: this.$t('Start Date'),
          required: false,
          colSize: 3,
          refMaxDate: 'subscriptionEndDate',
        },
        {
          id: 'subscriptionEndDate',
          type: 'date',
          label: this.$t('End Date'),
          required: false,
          colSize: 3,
          refMinDate: 'subscriptionStartDate',
        },
        {
          id: 'subscriptionPrice',
          type: 'text',
          label: this.$t('subscriptionPrice'),
          required: false,
          rules: 'positive',
          colSize: 3,
        },
        {
          id: 'balance',
          type: 'number',
          label: this.$t('Balance'),
          required: false,
          colSize: 3,
        },
      ]
    },
    extraActions() {
      return [
        {
          endpoint: 'reset-password/email',
          icon: 'SendIcon',
          title: 'Reset Password Email',
          renderCondition: {
            key: 'enabled',
            value: true,
          },
        },
      ]
    },
  },
  mounted() {
    this.$store.dispatch('app-common/fetchHospitals').then((response) => {
      this.hospitalOptions = response.data
    })
    this.$store.dispatch('app-common/fetchCountries').then((response) => {
      this.countryOptions = response.data
    })
    this.$store.dispatch('app-common/fetchWorkingGroups').then((response) => {
      this.workingGroupOptions = response.data
    })
    this.$store.dispatch('app-common/fetchPrimaryGroups').then((response) => {
      this.userGroupOptions = response.data
    })
    this.$store.dispatch('app-common/fetchSecondaryGroups').then((response) => {
      this.secondaryGroupOptions = response.data
    })
    this.$store.dispatch('app-common/fetchSpecialties').then((response) => {
      this.specialtiesOptions = response.data
    })
    this.$store.dispatch('app-common/fetchClinics').then((response) => {
      this.clinicOptions = response.data
    })
    this.$store.dispatch('app-common/fetchPrefectures').then((response) => {
      this.prefectureOptions = response.data
    })
    this.$store.dispatch('app-common/fetchProfessions').then((response) => {
      this.professionOptions = response.data
    })
    this.$store.dispatch('app-common/fetchJobPositions').then((response) => {
      this.positionOptions = response.data
    })
    this.$store.dispatch('app-common/fetchMemberTypes').then((response) => {
      this.memberTypeOptions = response.data
    })
  },
  methods: {
    updateEntity(entity) {
      this.hasErpId = !!entity.erpId && entity.memberType.id !== 4
    },
    extraValidation() {
      if (this.entity.id && !this.entity.changePassword) {
        delete this.entity.password
      }
      return ''
    },
  },
}
</script>
